import Grid from '@mui/material/Grid';

import Button, {
  ButtonSize,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import Icon, { type IconName } from '@/components/Icon/Icon';
import GridContainer from '@/components/Layout/GridContainer/GridContainer';
import SectionHeader from '@/components/SectionHeader/SectionHeader';
import { SpacingContainer } from '@/components/SpacingContainer/SpacingContainer';
import type { ContentStyle } from '@/contentful/contentful-exports';
import useGlobalStyles from '@/styles/global.styles';

import useStyles from './ActionCards.styles';

export interface ActionCard {
  iconName?: IconName;
  heading?: string;
  subheading?: string;
  cta?: { text: string; url: string };
}

export interface ActionCardsProps {
  style?: ContentStyle;
  eyebrow?: string;
  heading?: string;
  subheading?: string;
  cards?: ActionCard[];
}

export default function ActionCards({
  style,
  eyebrow,
  heading,
  subheading,
  cards,
}: ActionCardsProps) {
  const { classes: globalClasses } = useGlobalStyles({ fixed: true });
  const { classes, cx } = useStyles();

  const isContentOnDark = style === 'OnDark';

  const shouldRenderCardSubheadings = cards?.some((card) => card.subheading);
  const shouldRenderCardIcons = cards?.every((card) => card.iconName);
  const is2Card = cards?.length === 2;

  return (
    <SpacingContainer className={cx(classes.container, style)}>
      <GridContainer fixed>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <SectionHeader
            eyebrow={eyebrow}
            heading={heading}
            subheading={subheading}
            isOnDark={isContentOnDark}
            fixed
          />
        </Grid>
      </GridContainer>
      {cards && (
        <GridContainer fixed>
          <Grid size={12}>
            <div
              className={cx(
                classes.cardContainer,
                is2Card ? classes.cardContainer2 : classes.cardContainer3
              )}
            >
              {cards.map(
                (
                  {
                    iconName,
                    heading: cardHeading,
                    subheading: cardSubheading,
                    cta,
                  }: ActionCard,
                  index
                ) => (
                  // Action Card
                  // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                  <div className={cx(classes.card, style)} key={index}>
                    <div className={classes.cardContent}>
                      <div>
                        {shouldRenderCardIcons && (
                          <Icon
                            name={iconName as IconName}
                            width={56}
                            className={cx(classes.icon, 'primaryTextColor')}
                          />
                        )}

                        <h3
                          className={cx(
                            globalClasses.h3,
                            classes.cardHeading,
                            'primaryTextColor'
                          )}
                        >
                          {cardHeading}
                        </h3>
                        {shouldRenderCardSubheadings && (
                          <p
                            className={cx(
                              globalClasses.body,
                              classes.cardSubheading,
                              'accentTextColor'
                            )}
                          >
                            {cardSubheading}
                          </p>
                        )}
                      </div>
                      <Button
                        label={cta?.text}
                        href={cta?.url}
                        variant={ButtonVariant.SECONDARY}
                        className={classes.cardCta}
                        isOnDark={!isContentOnDark}
                        size={{
                          xs: ButtonSize.SM,
                          sm: ButtonSize.MD,
                        }}
                      />
                    </div>
                  </div>
                )
              )}
            </div>
          </Grid>
        </GridContainer>
      )}
    </SpacingContainer>
  );
}
